/*
    To add an agent add its id, setting to show its enabled state, and setting to show
    it's assigned group to the types below. Add these same settings to the
    'ISiteSettingsForSiteAdmin.ts' file, and a default value for the enabled setting to
    the sites.ts file.

    Add the agent itself to the 'agents' array, giving it a `displayName` getter and a
    `description` getter for the split _displayName/_description message pair. The rendered
    label is composed from that pair via getAgentLabel() (Messages.messageWithColonSeparator) —
    never a hardcoded ': '. getAgentLabel() also derives the '*' trial-site marker from
    requiresRegistration + availableInTrial, so the agent itself carries no marker flag. Message
    getters are used (not plain strings) so the i18n bundle is read lazily at render time rather
    than at module-init when it may not be ready.

    ---

    The recipe above is for a legacy agent whose settings are top-level keys of
    ISiteSettingsForSiteAdmin. A V2 agent (settingsNamespace: 'siteSettingsV2') differs:
    its enabledSiteSetting/groupIdSiteSetting keys live in the untyped siteSettingsV2 bag, so they are
    NOT added to ISiteSettingsForSiteAdmin.ts (doing so hits TS2344) and no default is seeded
    in Sites.ts (the monolith owns the default). See the 'pulseDefinitionAuthoringAssistant'
    entry below.
*/
import { Messages } from '~src/codegen/Localize';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';

import { Agent, AgentId, SettingsForAgents_enabled, SITE_SETTINGS_V2_NAMESPACE, V2SettingsForAgents_enabled } from './AgentSectionTypes';

export const agents: Agent[] = [
    {
    groupId: '',
    enabled: false,
    id: 'pulse',
    displayName: Messages.settings_agents_pulse_displayName,
    description: Messages.settings_agents_pulse_description,
    enabledSiteSetting:'generativeAiEnabled',
    groupIdSiteSetting: 'generativeAiGroupId',
    requiresRegistration: false,
    flag: 'generativeAiSiteControl',
    readyForGroupsFlag: 'agentsPulseGAIReadyForGroups',
    availableInTrial: true,
    availableInTableauAITrial: true,
  },
  {
    groupId: '',
    enabled: false,
    id: 'pulseEmbeddings',
    displayName: Messages.settings_agents_pulseEmbeddings_displayName,
    description: Messages.settings_agents_pulseEmbeddings_description,
    enabledSiteSetting:'generativeAiPulseExternalEmbeddingsEnabled',
    groupIdSiteSetting: 'generativeAiPulseExternalEmbeddingsGroupId',
    requiresRegistration: false,
    flag: 'ntbueMultilingualAdHocQa',
    readyForGroupsFlag: 'agentsPulseGAIReadyForGroups',
    availableInTrial: true,
    availableInTableauAITrial: true,
  },
  {
    groupId: '',
    enabled: false,
    id: 'pulseDiscover',
    displayName: Messages.settings_agents_pulseDiscover_displayName,
    description: Messages.settings_agents_pulseDiscover_description,
    enabledSiteSetting:'generativeAiPulseDiscoverEnabled',
    groupIdSiteSetting: 'generativeAiPulseDiscoverGroupId',
    requiresRegistration: true,
    flag: 'ntbueDiscoverFromHomepageGroup',
    readyForGroupsFlag: 'agentsPulseDiscoverReadyForGroupsEnabled',
    availableInTrial: true,
    availableInTableauAITrial: true,
  },
  {
    groupId: '',
    enabled: false,
    id: 'pulseDefinitionAuthoringAssistant',
    displayName: Messages.settings_agents_pulseAuthoringAssistant_displayName,
    description: Messages.settings_agents_pulseAuthoringAssistant_description,
    enabledSiteSetting: 'generativeAiPulseDefinitionAuthoringAssistantEnabled',
    groupIdSiteSetting: 'generativeAiPulseDefinitionAuthoringAssistantGroupId',
    settingsNamespace: SITE_SETTINGS_V2_NAMESPACE,
    requiresRegistration: true,
    flag: 'ntbueDefinitionAuthoringAssistantEnabled',
    readyForGroupsFlag: 'ntbueDefinitionAuthoringAssistantEnabled',
    availableInTrial: true,
    availableInTableauAITrial: true,
  },
  {
    groupId: '',
    enabled: false,
    id: 'webAuthoring',
    displayName: Messages.settings_agents_webAuthoring_displayName,
    description: Messages.settings_agents_webAuthoring_description,
    enabledSiteSetting:'generativeAiWebAuthoring',
    groupIdSiteSetting: 'generativeAiWebAuthoringGroupId',
    requiresRegistration: true,
    flag: 'webAuthoringAISettingEnabled',
    availableInTrial: true,
    availableInTableauAITrial: false,
  },
  {
    groupId: '',
    enabled: false,
    id: 'dashboardInsights',
    // displayName varies with the QA and beta-label flags across four split _displayName strings.
    displayName: () => {
        if (FeatureFlags.analyticsAssistantDashboardQAEnabled) {
            return FeatureFlags.analyticsAssistantDashboardBetaLabelEnabled
                ? Messages.settings_agents_dashboard_insights_qa_beta_displayName()
                : Messages.settings_agents_dashboard_insights_qa_displayName();
        }
        return FeatureFlags.analyticsAssistantDashboardBetaLabelEnabled
            ? Messages.settings_agents_dashboard_insights_beta_no_qa_displayName()
            : Messages.settings_agents_dashboard_insights_no_qa_displayName();
    },
    // description varies with the QA flag only — the beta and non-beta descriptions are identical.
    description: () => FeatureFlags.analyticsAssistantDashboardQAEnabled
        ? Messages.settings_agents_dashboard_insights_qa_description()
        : Messages.settings_agents_dashboard_insights_no_qa_description(),
    enabledSiteSetting:'dashboardNarrativeEnabled',
    groupIdSiteSetting: 'dashboardInsightsGroupId',
    requiresRegistration: true,
    flag: 'analyticsAssistantDashboardInsightsEnabled',
    availableInTrial: true,
    availableInTableauAITrial: false,
    betaFlag: 'analyticsAssistantDashboardBetaLabelEnabled',
    dashboardQAFlag: 'analyticsAssistantDashboardQAEnabled'
  },
  {
    groupId: '',
    enabled: false,
    id: 'prep',
    displayName: () => FeatureFlags.einsteinCopilotForPrep
        ? Messages.settings_agents_prep_flow_displayName()
        : Messages.settings_agents_prep_displayName(),
    description: () => FeatureFlags.einsteinCopilotForPrep
        ? Messages.settings_agents_prep_flow_description()
        : Messages.settings_agents_prep_description(),
    enabledSiteSetting:'generativeAiPrep',
    groupIdSiteSetting: 'generativeAiPrepGroupId',
    requiresRegistration: true,
    availableInTrial: true,
    availableInTableauAITrial: true,
  },
  {
    groupId: '',
    enabled: false,
    id: 'catalog',
    displayName: Messages.settings_agents_catalog_displayName,
    description: Messages.settings_agents_catalog_description,
    enabledSiteSetting:'generativeAiDataCatalog',
    groupIdSiteSetting: 'generativeAiDataCatalogGroupId',
    requiresRegistration: true,
    readyForGroupsFlag: 'agentsCatalogReadyForGroups',
    availableInTrial: false,
    availableInTableauAITrial: false,
  },
  {
    groupId: '',
    enabled: false,
    id: 'tableauKnowledge',
    displayName: Messages.settings_tableau_knowledge_displayName,
    description: Messages.settings_tableau_knowledge_description,
    enabledSiteSetting: 'tableauKnowledgeActivated',
    // Tableau Knowledge has no group-based access support.
    groupIdSiteSetting: null,
    settingsNamespace: SITE_SETTINGS_V2_NAMESPACE,
    requiresRegistration: true,
    flag: 'tableauKnowledgeNavEnabled',
    availableInTrial: false,
    availableInTableauAITrial: false,
  },
  {
    groupId: '',
    enabled: false,
    id: 'tableauAiSearch',
    displayName: Messages.settings_agents_tableauAiSearch_displayName,
    description: Messages.settings_agents_tableauAiSearch_description,
    enabledSiteSetting: 'tableauAiSearchEnabled',
    groupIdSiteSetting: 'tableauAiSearchGroupId',
    settingsNamespace: SITE_SETTINGS_V2_NAMESPACE,
    requiresRegistration: true,
    flag: 'ces_AISearchEnabled',
    availableInTrial: false,
    availableInTableauAITrial: false,
  },
];

// Agents shown on Server. Catalog is excluded: it is not available on-prem.
// prep's per-group access is enforced only in the UI (via a server-computed canUse boolean);
// there is no request-time CES enforcement for it. tableauAiSearch differs: CES's
// isAISearchSiteSettingEnabled honors both tableauAiSearchEnabled and the tableauAiSearchGroupId
// allow-list in every deployment, so the group written here is enforced at request time on Server.
const SERVER_SUPPORTED_AGENT_IDS: AgentId[] = ['webAuthoring', 'dashboardInsights', 'prep', 'tableauAiSearch'];

// On Server: keep only Server-supported agents and remap their enable keys to the on-prem
// settings. The groupId keys are the persisted columns and are left unchanged.
// On Cloud: return the array unchanged (identity) so existing behavior is byte-for-byte the same.
export const selectServerAgents = (all: Agent[], isOnline: boolean): Agent[] => {
    if (isOnline) {
        return all;
    }
    return all
        .filter(a => SERVER_SUPPORTED_AGENT_IDS.indexOf(a.id) !== -1)
        .map(a => {
            if (a.id === 'webAuthoring') {
                // Clear `flag` on-prem: webAuthoring's gate (webAuthoringAISettingEnabled) is
                // driven by LaunchDarkly/Gater, which only exists in the Online environment, so on
                // Server it always resolves false and AgentRow's `if (agent.flag && !FeatureFlags[
                // agent.flag]) return null` would hide the row. On-prem the row is ungated, so it
                // must render unconditionally.
                return { ...a, enabledSiteSetting: 'generativeAiWebAuthoringOnPremEnabled' as SettingsForAgents_enabled, flag: undefined };
            }
            if (a.id === 'dashboardInsights') {
                return {
                    ...a,
                    enabledSiteSetting: 'dashboardQAEnabled' as V2SettingsForAgents_enabled,
                    enabledNamespace: SITE_SETTINGS_V2_NAMESPACE,
                    // groupIdSiteSetting stays 'dashboardInsightsGroupId' (top-level, per the persisted column)
                };
            }
            // prep and tableauAiSearch need no remap: their on-prem enable/groupId keys are identical
            // to Cloud's (prep: top-level 'generativeAiPrep'/'generativeAiPrepGroupId'; tableauAiSearch:
            // siteSettingsV2 'tableauAiSearchEnabled'/'tableauAiSearchGroupId'). tableauAiSearch's
            // `flag` (ces_AISearchEnabled) is kept: that archer flag exists on-prem and gates the row.
            return a;
        });
};
